/**
 * Phase 14.5 Step 2 — pure dispatch helper.
 *
 * Translates the wizard's five boolean dimension toggles into a
 * single `{campaign_type, dispatch_endpoint, blocking_errors}` triple.
 *
 * Contract per PHASE_14_5_PLAN.md §2.2 — four valid lanes:
 *   • baseline       → POST /api/pk-campaigns       (Phase 10 / 10.1)
 *   • sensitivity    → POST /api/pk-campaigns       (Phase 11)
 *   • target_pk      → POST /api/pk-campaigns       (Phase 12)
 *   • geometry_sweep → POST /api/geometry-campaigns (Phase 13 / 14)
 *
 * Mutual-exclusion rules surface as operator-friendly strings in
 * `blocking_errors[]`. The wizard's submit button is disabled while
 * `blocking_errors.length > 0`.
 *
 * No I/O. No React. Fully unit-testable.
 */
import { applyPreset as applyPerturbationPreset } from "@/components/pk/perturbationPresets";

export const VALID_CAMPAIGN_TYPES = Object.freeze([
    "baseline",
    "sensitivity",
    "target_pk",
    "geometry_sweep",
    "optimisation",
]);

export const PK_DISPATCH_ENDPOINT = "/api/pk-campaigns";
export const GEOMETRY_DISPATCH_ENDPOINT = "/api/geometry-campaigns";
export const OPTIMISATION_DISPATCH_ENDPOINT = "/api/optimisation-campaigns";

/**
 * @param {{
 *   perturbation?: boolean,
 *   sensitivity?: boolean,
 *   target_pk?: boolean,
 *   geometry?: boolean,
 *   degradation?: boolean,
 * }} config
 * @returns {{
 *   campaign_type: string|null,
 *   dispatch_endpoint: string|null,
 *   blocking_errors: string[],
 * }}
 */
export function deriveCampaignType(config) {
    const c = config || {};
    const perturbation = !!c.perturbation;
    const sensitivity = !!c.sensitivity;
    const target_pk = !!c.target_pk;
    const geometry = !!c.geometry;
    const degradation = !!c.degradation;
    const optimisation = !!c.optimisation;

    const blocking_errors = [];

    // Optimisation is a single-dimension lane in v1 — it owns the
    // dispatch and cannot be combined with the other dimensions.
    if (optimisation && (perturbation || sensitivity || target_pk || geometry || degradation)) {
        blocking_errors.push(
            "Optimisation cannot be combined with other dimensions in v1.",
        );
    }

    // Mutual exclusion #1 — geometry × pk-campaign-type
    if (geometry && target_pk) {
        blocking_errors.push(
            "Target Pk and Geometry Sweep cannot be combined in v1.",
        );
    }
    if (geometry && sensitivity) {
        blocking_errors.push(
            "Sensitivity and Geometry Sweep cannot be combined in v1.",
        );
    }

    // Mutual exclusion #2 — sensitivity XOR target_pk
    if (sensitivity && target_pk) {
        blocking_errors.push(
            "Sensitivity and Target Pk cannot be combined in v1.",
        );
    }

    // Per Y2.example — sensitivity requires perturbation enabled
    if (sensitivity && !perturbation) {
        blocking_errors.push(
            "Sensitivity requires Perturbation to be enabled.",
        );
    }

    // Per Y2.example — degradation requires geometry
    if (degradation && !geometry) {
        blocking_errors.push(
            "Degradation profile can only be applied to Geometry Sweep in v1.",
        );
    }

    // At least one campaign-defining dimension required
    if (!perturbation && !sensitivity && !target_pk && !geometry && !optimisation) {
        blocking_errors.push(
            "Select at least one dimension to define a campaign.",
        );
    }

    let campaign_type = null;
    let dispatch_endpoint = null;
    if (blocking_errors.length === 0) {
        if (optimisation) {
            campaign_type = "optimisation";
            dispatch_endpoint = OPTIMISATION_DISPATCH_ENDPOINT;
        } else if (geometry) {
            campaign_type = "geometry_sweep";
            dispatch_endpoint = GEOMETRY_DISPATCH_ENDPOINT;
        } else if (target_pk) {
            campaign_type = "target_pk";
            dispatch_endpoint = PK_DISPATCH_ENDPOINT;
        } else if (sensitivity) {
            campaign_type = "sensitivity";
            dispatch_endpoint = PK_DISPATCH_ENDPOINT;
        } else if (perturbation) {
            campaign_type = "baseline";
            dispatch_endpoint = PK_DISPATCH_ENDPOINT;
        }
    }

    return { campaign_type, dispatch_endpoint, blocking_errors };
}

/**
 * Phase 14.5 Step 4 — payload assembler.
 *
 * Pure function: takes the wizard's react-hook-form value object and
 * a resolved dispatch lane, returns the lane-specific POST body
 * matching the live OpenAPI schema (verified against
 * `/api/openapi.json` at Step 4 design time).
 *
 * Lane → endpoint → payload shape:
 *   • baseline       → POST /api/pk-campaigns       (PkCampaignCreate)
 *   • sensitivity    → POST /api/pk-campaigns       (+ sensitivity_config)
 *   • target_pk      → POST /api/pk-campaigns       (+ target_pk_config)
 *   • geometry_sweep → POST /api/geometry-campaigns (GeometryCampaignCreate)
 *
 * Phase 14.5 Step 6 (Y2d) — perturbation preset → overrides
 * translation now happens here. Pass `options.pkResolvedSpecs`
 * (resolved against `pk-campaigns/registry?scenario_id=…`) and the
 * three pk-side lanes (baseline / sensitivity / target_pk) emit a
 * preset-shaped `perturbation_overrides` object instead of the
 * empty `{}` the Step 4 stub used to ship. `custom` preset still
 * falls back to operator-supplied `formValues.perturbation.overrides`.
 *
 * No I/O. No React. Fully unit-testable.
 *
 * @param {object} formValues   Wizard form state (react-hook-form)
 * @param {string} lane         Resolved dispatch lane from `deriveCampaignType`
 * @param {object} [options]    Optional helpers
 * @param {object} [options.pkResolvedSpecs]  pk-campaigns registry `resolved_specs` map
 * @returns {object|null}       Lane-specific POST body, or null on unknown lane
 */
export function assemblePayload(formValues, lane, options = {}) {
    const v = formValues || {};
    const pkResolvedSpecs = options?.pkResolvedSpecs || null;
    const baseFields = {
        scenario_id: v.scenario_id,
        name: (v.name || "").trim(),
    };

    // Phase 14.5 Step 6 (Y2d) — preset → overrides translation.
    // Pure helper; returns the empty-object back-compat behaviour
    // when the section is off OR when no resolved_specs was supplied
    // (registry not yet loaded). `custom` preset routes through the
    // operator-supplied `formValues.perturbation.overrides` map.
    const _resolvePerturbationOverrides = () => {
        const pert = v.perturbation || {};
        if (!pert.enabled) return {};
        const preset = pert.preset || "nominal";
        if (preset === "custom") {
            return pert.overrides && typeof pert.overrides === "object" ? pert.overrides : {};
        }
        if (!pkResolvedSpecs) {
            // Registry didn't supply resolved_specs (offline test path or
            // registry rejected). Fall back to empty overrides so the
            // backend resolves every variable to its platform default.
            return {};
        }
        return applyPerturbationPreset(preset, pkResolvedSpecs);
    };

    if (lane === "baseline") {
        return {
            ...baseFields,
            campaign_type: "baseline",
            // Phase 14.5 Step 6 (Y2d) — preset-driven overrides.
            // Pre-Y2d this was always `{}`; now the wizard's
            // perturbation preset is honoured when the section is on.
            perturbation_overrides: _resolvePerturbationOverrides(),
            // Phase 14.9 W2 — campaign-level airframe overrides.
            ...(v.interceptor_airframe_id
                ? { interceptor_airframe_id: v.interceptor_airframe_id } : {}),
            ...(v.target_airframe_id
                ? { target_airframe_id: v.target_airframe_id } : {}),
        };
    }

    if (lane === "sensitivity") {
        const sens = v.sensitivity || {};
        return {
            ...baseFields,
            campaign_type: "sensitivity",
            sensitivity_config: {
                method: sens.method || "variance_first_order",
                samples_per_variable: Number(sens.samples_per_variable ?? 20),
                ...(Array.isArray(sens.variable_subset) && sens.variable_subset.length > 0
                    ? { variable_subset: sens.variable_subset }
                    : {}),
            },
            perturbation_overrides: _resolvePerturbationOverrides(),
            // Phase 14.9 W2 — campaign-level airframe overrides.
            ...(v.interceptor_airframe_id
                ? { interceptor_airframe_id: v.interceptor_airframe_id } : {}),
            ...(v.target_airframe_id
                ? { target_airframe_id: v.target_airframe_id } : {}),
        };
    }

    if (lane === "target_pk") {
        const tpk = v.target_pk || {};
        return {
            ...baseFields,
            campaign_type: "target_pk",
            target_pk_config: {
                target_pk: Number(tpk.target_pk ?? 0.6),
                max_iterations: Number(tpk.max_iterations ?? 30),
                samples_per_iteration: Number(tpk.samples_per_iteration ?? 20),
                lambda_penalty: Number(tpk.lambda_penalty ?? 0.1),
                ...(Array.isArray(tpk.variable_budget) && tpk.variable_budget.length > 0
                    ? { variable_budget: tpk.variable_budget }
                    : {}),
            },
            perturbation_overrides: _resolvePerturbationOverrides(),
            // Phase 14.9 W2 — campaign-level airframe overrides.
            ...(v.interceptor_airframe_id
                ? { interceptor_airframe_id: v.interceptor_airframe_id } : {}),
            ...(v.target_airframe_id
                ? { target_airframe_id: v.target_airframe_id } : {}),
        };
    }

    if (lane === "geometry_sweep") {
        const geom = v.geometry || {};
        const env = geom.envelope || {};
        return {
            ...baseFields,
            campaign_type: "geometry_sweep",
            envelope: {
                range_m: env.range_m || { lo: 0, hi: 1 },
                bearing_deg: env.bearing_deg || { lo: 0, hi: 1 },
                altitude_m: env.altitude_m || { lo: 0, hi: 1 },
                approach_vector_deg: env.approach_vector_deg || { lo: 0, hi: 1 },
            },
            sampler: { kind: geom.sampler || "uniform" },
            n_points: Number(geom.n_points ?? 50),
            samples_per_point: Number(geom.samples_per_point ?? 1),
            ...(v.degradation?.enabled && v.degradation?.profile_id
                ? { degradation_profile_id: v.degradation.profile_id }
                : {}),
            // Phase 14.9 W2 — campaign-level airframe overrides.
            ...(v.interceptor_airframe_id
                ? { interceptor_airframe_id: v.interceptor_airframe_id } : {}),
            ...(v.target_airframe_id
                ? { target_airframe_id: v.target_airframe_id } : {}),
        };
    }

    if (lane === "optimisation") {
        const opt = v.optimisation || {};
        return {
            ...baseFields,
            ga_config: {
                population_size: Number(opt.population_size ?? 32),
                generations: Number(opt.generations ?? 15),
                mc_seeds_per_individual: Number(opt.mc_seeds_per_individual ?? 20),
                max_workers: Number(opt.max_workers ?? 4),
                seed: Number(opt.seed ?? 42),
            },
            // Phase 14.9 W2 — campaign-level airframe overrides.
            ...(v.interceptor_airframe_id
                ? { interceptor_airframe_id: v.interceptor_airframe_id } : {}),
            ...(v.target_airframe_id
                ? { target_airframe_id: v.target_airframe_id } : {}),
        };
    }

    return null;
}

export default deriveCampaignType;
